import { PlusIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { type DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { Input } from "@ui/input";
import { Label } from "@ui/label";
import { Select } from "@ui/select";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { SortableList, SortableItem } from "@/components/ui/sortable";
import { ValidationError } from "@/components/ui/validation-error";
import type { SelectOption } from "@ui/select/single-select";
import { RadioGroup, type RadioOption } from "@/components/ui/radio-group";
import CodeToggle from "@ui/code-toggle";
import CodeEditButton from "@ui/button/code-edit-button";
import { useAtom } from "jotai";
import { builderStoreAtom } from "@/store";
import { useBpmDatasets } from "@/hooks/useMasterData";
import type { Operator } from "@/types/flow";
import {
    createStaticOption,
    normalizeStaticDatasource,
    type DropdownHeaderType,
    type StaticDatasource,
    type StaticOption,
    type DynamicDatasource,
    type DynamicDatasourceFilterUi,
    type DynamicDatasourceFilterExpression,
} from "./types";
import { cn } from "@/utils/cn";
import { ExpressionDefault, ExpressionType } from "@/const/expression";
import { GridHeaderVisibilityFields } from "./visibility-fields";

const sortingOrderOptions: RadioOption[] = [
    { value: "asc", label: "Ascending (A-Z)" },
    { value: "desc", label: "Descending (Z-A)" },
];

export const displayOptionChoices = [
    { value: "label", label: "Label Only" },
    { value: "label_value", label: "Label + Value" },
];

const DEFAULT_GRID_FILTER_TEMPLATE = ExpressionDefault[ExpressionType.GridDropdownFilter];

const operatorOptions: SelectOption<Operator>[] = [
    { label: ">", value: ">", key: ">" },
    { label: "<", value: "<", key: "<" },
    { label: ">=", value: ">=", key: ">=" },
    { label: "<=", value: "<=", key: "<=" },
    { label: "==", value: "==", key: "==" },
    { label: "!=", value: "!=", key: "!=" },
    { label: "Contains", value: "contains", key: "contains" },
    { label: "Not Contains", value: "not_contains", key: "not_contains" },
    { label: "Equals", value: "equals", key: "equals" },
    { label: "Not Equals", value: "not_equals", key: "not_equals" },
];

type DatasourceType = "static" | "dynamic";

const datasourceTypeOptions: RadioOption[] = [
    { value: "static", label: "Static" },
    { value: "dynamic", label: "Table/View" },
];

export function DropdownHeaderFields({
    header,
    updateHeader,
    headerError,
}: DropdownHeaderType) {
    const [expandedOptionIndexes, setExpandedOptionIndexes] = useState<number[]>(
        [],
    );

    const currentDatasourceType: DatasourceType =
        (header.datasource as { type?: string } | undefined)?.type === "dynamic"
            ? "dynamic"
            : "static";

    const staticDatasource = normalizeStaticDatasource(
        header.datasource,
        header.keyValue,
    );
    const staticOptions = staticDatasource?.options ?? [];
    const defaultDropdownValue =
        staticDatasource?.defaultValue &&
            !staticDatasource.defaultValue.isReference &&
            typeof staticDatasource.defaultValue.value === "string"
            ? staticDatasource.defaultValue.value
            : "__none__";
    const dropdownDefaultValueOptions: SelectOption<string>[] = [
        { label: "None", value: "__none__", key: "none" },
        ...staticOptions,
    ];

    const updateStaticDatasource = (
        updater: (current: StaticDatasource) => StaticDatasource,
    ) => {
        const currentDatasource = normalizeStaticDatasource(
            header.datasource,
            header.keyValue,
        );
        const nextDatasource = updater(currentDatasource);
        updateHeader({ datasource: nextDatasource });
    };

    const handleDatasourceTypeChange = (type: DatasourceType) => {
        if (type === "static") {
            updateHeader({
                datasource: normalizeStaticDatasource(
                    undefined,
                    header.keyValue,
                ),
            });
        } else {
            updateHeader({
                datasource: {
                    type: "dynamic" as const,
                    table: { tableKey: null, labelKey: null, valueKey: null },
                } satisfies DynamicDatasource,
            });
        }
    };

    const handleDropdownOptionsDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = staticOptions.findIndex(
            (option) => option.key === active.id,
        );
        const newIndex = staticOptions.findIndex(
            (option) => option.key === over.id,
        );
        if (oldIndex < 0 || newIndex < 0) return;

        updateStaticDatasource((current) => ({
            ...current,
            options: arrayMove(current.options, oldIndex, newIndex),
        }));

        setExpandedOptionIndexes((prev) =>
            prev.map((i) => {
                if (i === oldIndex) return newIndex;
                if (oldIndex < newIndex && i > oldIndex && i <= newIndex) return i - 1;
                if (oldIndex > newIndex && i >= newIndex && i < oldIndex) return i + 1;
                return i;
            }),
        );
    };

    return (
        <GridHeaderVisibilityFields header={header} updateHeader={updateHeader}>
            {"placeholder" in header && (
                <div>
                    <Label>Placeholder</Label>
                    <Input
                        value={(header.placeholder as string) ?? ""}
                        placeholder="Placeholder"
                        onChange={(e) => updateHeader({ placeholder: e.target.value })}
                    />
                </div>
            )}

            <div className="space-y-2">
                <Label className="font-body-medium-medium block">
                    Data Source Type
                </Label>
                <RadioGroup
                    name="grid-dropdown-datasource-type"
                    value={currentDatasourceType}
                    onChange={(val) =>
                        handleDatasourceTypeChange(val as DatasourceType)
                    }
                    options={datasourceTypeOptions}
                    className="flex gap-4"
                />
            </div>

            <div className="flex items-center space-x-2">
                <Checkbox
                    id={`grid-dropdown-${header.key}-search-in-options`}
                    checked={header.searchInOptions ?? false}
                    onCheckedChange={(checked) =>
                        updateHeader({ searchInOptions: checked === true })
                    }
                />
                <label
                    htmlFor={`grid-dropdown-${header.key}-search-in-options`}
                    className="text-sm font-normal cursor-pointer"
                >
                    Search in Options
                </label>
            </div>

            {currentDatasourceType === "static" && (
                <StaticDropdownFields
                    staticOptions={staticOptions}
                    expandedOptionIndexes={expandedOptionIndexes}
                    setExpandedOptionIndexes={setExpandedOptionIndexes}
                    updateStaticDatasource={updateStaticDatasource}
                    handleDropdownOptionsDragEnd={handleDropdownOptionsDragEnd}
                    dropdownDefaultValueOptions={dropdownDefaultValueOptions}
                    defaultDropdownValue={defaultDropdownValue}
                    header={header}
                    updateHeader={updateHeader}
                />
            )}

            {currentDatasourceType === "dynamic" && (
                <DynamicDropdownFields
                    header={header}
                    updateHeader={updateHeader}
                />
            )}

            {headerError && <ValidationError>{headerError}</ValidationError>}
        </GridHeaderVisibilityFields>
    );
}

function StaticDropdownFields({
    staticOptions,
    expandedOptionIndexes,
    setExpandedOptionIndexes,
    updateStaticDatasource,
    handleDropdownOptionsDragEnd,
    dropdownDefaultValueOptions,
    defaultDropdownValue,
    header,
    updateHeader,
}: {
    staticOptions: StaticOption[];
    expandedOptionIndexes: number[];
    setExpandedOptionIndexes: React.Dispatch<React.SetStateAction<number[]>>;
    updateStaticDatasource: (
        updater: (current: StaticDatasource) => StaticDatasource,
    ) => void;
    handleDropdownOptionsDragEnd: (event: DragEndEvent) => void;
    dropdownDefaultValueOptions: SelectOption<string>[];
    defaultDropdownValue: string;
    header: DropdownHeaderType["header"];
    updateHeader: DropdownHeaderType["updateHeader"];
}) {
    return (
        <>
            <div className="space-y-2">
                <div className="flex flex-row justify-between items-center">
                    <Label>Options</Label>
                    <div className="flex items-center gap-2">
                        <div className="text-xs text-white bg-giant-blue rounded-full w-5 h-5 flex items-center justify-center">
                            {staticOptions.length}
                        </div>
                        <Button
                            variant="ghost"
                            className="p-0 cursor-pointer"
                            onClick={() => {
                                updateStaticDatasource((current) => ({
                                    ...current,
                                    options: [
                                        ...current.options,
                                        createStaticOption(
                                            current.options.length,
                                            header.keyValue,
                                        ),
                                    ],
                                }));
                            }}
                        >
                            <PlusIcon className="text-giant-blue" />
                        </Button>
                    </div>
                </div>

                <SortableList
                    items={staticOptions.map((option) => ({
                        id: option.key,
                    }))}
                    onDragEnd={handleDropdownOptionsDragEnd}
                >
                    {staticOptions.map((option, index) => (
                        <SortableItem
                            key={option.key}
                            id={option.key}
                            expanded={expandedOptionIndexes.includes(index)}
                            onExpand={() => {
                                setExpandedOptionIndexes((prev) =>
                                    prev.includes(index)
                                        ? prev.filter((i) => i !== index)
                                        : [...prev, index],
                                );
                            }}
                            onRemove={() => {
                                if (staticOptions.length <= 1) return;

                                updateStaticDatasource((current) => {
                                    const nextOptions = current.options.filter(
                                        (_, i) => i !== index,
                                    );
                                    const optionValues = new Set(
                                        nextOptions.map((item) => item.value),
                                    );
                                    const currentDefault =
                                        current.defaultValue &&
                                            !current.defaultValue.isReference &&
                                            typeof current.defaultValue.value === "string"
                                            ? current.defaultValue.value
                                            : undefined;

                                    return {
                                        ...current,
                                        options: nextOptions,
                                        defaultValue:
                                            currentDefault && !optionValues.has(currentDefault)
                                                ? undefined
                                                : current.defaultValue,
                                    };
                                });

                                setExpandedOptionIndexes((prev) =>
                                    prev
                                        .filter((i) => i !== index)
                                        .map((i) => (i > index ? i - 1 : i)),
                                );
                            }}
                            title={
                                <span className="font-medium overflow-hidden text-ellipsis whitespace-nowrap">
                                    {option.label || option.value}
                                </span>
                            }
                        >
                            {expandedOptionIndexes.includes(index) && (
                                <div className="mt-3 space-y-4 pt-3 border-t border-[#DFE4EA]">
                                    <div className="space-y-2">
                                        <Label className="text-base font-medium text-dark">
                                            Label
                                        </Label>
                                        <Input
                                            value={option.label}
                                            placeholder={`Option ${index + 1}`}
                                            onChange={(e) => {
                                                const nextLabel = e.target.value;
                                                updateStaticDatasource((current) => ({
                                                    ...current,
                                                    options: current.options.map((item, i) =>
                                                        i === index
                                                            ? { ...item, label: nextLabel }
                                                            : item,
                                                    ),
                                                }));
                                            }}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-base font-medium text-dark">
                                            Value
                                        </Label>
                                        <Input
                                            value={option.value}
                                            placeholder={`option_${index + 1}_${header.keyValue}`}
                                            onChange={(e) => {
                                                const nextValue = e.target.value;
                                                updateStaticDatasource((current) => {
                                                    const previousValue =
                                                        current.options[index]?.value;
                                                    const nextOptions = current.options.map((item, i) =>
                                                        i === index
                                                            ? { ...item, value: nextValue }
                                                            : item,
                                                    );
                                                    const currentDefault =
                                                        current.defaultValue &&
                                                            !current.defaultValue.isReference &&
                                                            typeof current.defaultValue.value === "string"
                                                            ? current.defaultValue.value
                                                            : undefined;

                                                    return {
                                                        ...current,
                                                        options: nextOptions,
                                                        defaultValue:
                                                            currentDefault === previousValue
                                                                ? {
                                                                    isReference: false,
                                                                    value: nextValue,
                                                                }
                                                                : current.defaultValue,
                                                    };
                                                });
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </SortableItem>
                    ))}
                </SortableList>
            </div>

            <div className="space-y-2">
                <Label>Default Value</Label>
                <Select
                    mode="single"
                    options={dropdownDefaultValueOptions}
                    value={defaultDropdownValue}
                    placeholder="Select default value"
                    onChange={(value) =>
                        updateStaticDatasource((current) => ({
                            ...current,
                            defaultValue:
                                value && value !== "__none__"
                                    ? { isReference: false, value }
                                    : undefined,
                        }))
                    }
                />
            </div>

            <div className="space-y-2">
                <Label className="font-body-medium-medium block">
                    Display Option
                </Label>
                <RadioGroup
                    name="static-display-option"
                    value={
                        (header.datasource as StaticDatasource | undefined)
                            ?.displayOption ?? "label"
                    }
                    onChange={(val) =>
                        updateStaticDatasource((current) => ({
                            ...current,
                            displayOption: val as "label" | "label_value",
                        }))
                    }
                    options={displayOptionChoices}
                />
            </div>
        </>
    );
}

function DynamicDropdownFields({
    header,
    updateHeader,
}: {
    header: DropdownHeaderType["header"];
    updateHeader: DropdownHeaderType["updateHeader"];
}) {
    const { datasets: allTables, isLoading: isLoadingTables } = useBpmDatasets();

    const dynamicDatasource = header.datasource as DynamicDatasource | undefined;

    const tableOptions: SelectOption<string>[] = useMemo(
        () =>
            allTables.map((table) => ({
                label: table.name,
                value: table.code,
                key: table.code,
            })),
        [allTables],
    );

    const validTableKey = useMemo(() => {
        const currentTableKey = dynamicDatasource?.table?.tableKey;
        if (!currentTableKey) return undefined;
        return tableOptions.find((option) => option.value === currentTableKey)
            ?.value;
    }, [tableOptions, dynamicDatasource?.table?.tableKey]);

    const columnOptions: SelectOption<string>[] = useMemo(() => {
        const fields = allTables.find(
            (table) => table.code === dynamicDatasource?.table?.tableKey,
        )?.fields;
        return (
            fields?.map((field) => ({
                label: field.name,
                value: field.name,
                key: field.name,
            })) ?? []
        );
    }, [allTables, dynamicDatasource?.table?.tableKey]);

    const updateDynamicTable = (next: {
        tableKey?: string | null;
        labelKey?: string | null;
        valueKey?: string | null;
    }) => {
        const current = dynamicDatasource?.table ?? {
            tableKey: null,
            labelKey: null,
            valueKey: null,
        };

        updateHeader({
            datasource: {
                type: "dynamic" as const,
                ...dynamicDatasource,
                table: { ...current, ...next },
            },
        });
    };

    return (
        <div className="space-y-4">
            <div className="justify-start text-secondary-text text-sm font-medium">
                Table Configuration
            </div>

            <div className="self-stretch flex flex-col justify-start items-start gap-1">
                <Label
                    className="text-dark text-base font-medium"
                    aria-required
                >
                    Source Name
                </Label>
                <Select
                    options={tableOptions}
                    value={validTableKey}
                    placeholder={
                        isLoadingTables ? "Loading tables..." : "Select a table"
                    }
                    onChange={(nextTableKey: string) => {
                        updateHeader({
                            datasource: {
                                type: "dynamic" as const,
                                table: {
                                    tableKey: nextTableKey,
                                    valueKey: null,
                                    labelKey: null,
                                },
                            } satisfies DynamicDatasource,
                        });
                    }}
                />
            </div>

            {validTableKey && (
                <>
                    <div className="self-stretch flex flex-col justify-start items-start gap-1">
                        <Label
                            className="text-dark text-base font-medium"
                            aria-required
                        >
                            Label Column
                        </Label>
                        <Select
                            options={columnOptions}
                            value={dynamicDatasource?.table?.labelKey ?? undefined}
                            placeholder="Select a table column"
                            onChange={(nextLabelKey: string) =>
                                updateDynamicTable({ labelKey: nextLabelKey })
                            }
                        />
                    </div>

                    <div className="self-stretch flex flex-col justify-start items-start gap-1">
                        <Label
                            className="text-dark text-base font-medium"
                            aria-required
                        >
                            Value Column
                        </Label>
                        <Select
                            options={columnOptions}
                            value={dynamicDatasource?.table?.valueKey ?? undefined}
                            placeholder="Select a table column"
                            onChange={(nextValueKey: string) =>
                                updateDynamicTable({ valueKey: nextValueKey })
                            }
                        />
                    </div>

                    <div className="justify-start text-secondary-text text-sm font-medium">
                        Sorting Configuration
                    </div>

                    <div className="self-stretch flex flex-col justify-start items-start gap-1">
                        <Label className="text-dark text-base font-medium">
                            Sorting Column
                        </Label>
                        <Select
                            options={columnOptions}
                            value={dynamicDatasource?.sorter?.columnKey}
                            placeholder="Select a table column"
                            onChange={(nextSortColumn: string) =>
                                updateHeader({
                                    datasource: {
                                        ...dynamicDatasource,
                                        type: "dynamic" as const,
                                        sorter: {
                                            ...(dynamicDatasource?.sorter ?? {}),
                                            columnKey: nextSortColumn,
                                        },
                                    },
                                })
                            }
                        />
                    </div>

                    <div
                        className={cn(
                            "self-stretch flex flex-col justify-start items-start gap-1 pointer-events-none opacity-30",
                            dynamicDatasource?.sorter?.columnKey &&
                            "pointer-events-auto opacity-100",
                        )}
                    >
                        <Label className="text-dark text-base font-medium">
                            Sorting Order
                        </Label>
                        <div className="pl-5">
                            <RadioGroup
                                name="grid-dropdown-sorting-order"
                                value={dynamicDatasource?.sorter?.order}
                                onChange={(nextOrder) =>
                                    updateHeader({
                                        datasource: {
                                            ...dynamicDatasource,
                                            type: "dynamic" as const,
                                            sorter: {
                                                ...(dynamicDatasource?.sorter ?? {}),
                                                order: nextOrder as "asc" | "desc",
                                            },
                                        },
                                    })
                                }
                                options={sortingOrderOptions}
                                disabled={!dynamicDatasource?.sorter?.columnKey}
                            />
                        </div>
                    </div>

                    <GridDropdownFilterSection
                        filter={dynamicDatasource?.filter}
                        columnOptions={columnOptions}
                        onChangeFilter={(nextFilter) =>
                            updateHeader({
                                datasource: {
                                    ...dynamicDatasource,
                                    type: "dynamic" as const,
                                    filter: nextFilter,
                                },
                            })
                        }
                    />
                    <div className="space-y-2">
                        <Label className="font-body-medium-medium block">
                            Display Option
                        </Label>
                        <RadioGroup
                            name="dynamic-display-option"
                            value={dynamicDatasource?.displayOption ?? "label"}
                            onChange={(val) =>
                                updateHeader({
                                    datasource: {
                                        ...dynamicDatasource,
                                        type: "dynamic" as const,
                                        displayOption: val as "label" | "label_value",
                                    },
                                })
                            }
                            options={displayOptionChoices}
                        />
                    </div>
                </>
            )}
        </div>
    );
}

function GridDropdownFilterSection({
    filter,
    columnOptions,
    onChangeFilter,
}: {
    filter?: DynamicDatasource["filter"];
    columnOptions: SelectOption<string>[];
    onChangeFilter: (next: DynamicDatasource["filter"]) => void;
}) {
    const [bStore] = useAtom(builderStoreAtom);

    const formFieldOptions: SelectOption<string>[] = useMemo(() => {
        return Object.values(bStore?.getSchema().entities ?? {}).map(
            (entity) => ({
                label:
                    (entity.attributes.label.isReference
                        ? entity.attributes.label.reference
                        : entity.attributes.label.value) ?? "Unknown",
                value: entity.attributes.name,
                key: entity.attributes.name,
            }),
        );
    }, [bStore]);

    const getCurrentUiFilter = (): DynamicDatasourceFilterUi => {
        if (filter && !filter.isReference) {
            return {
                isReference: false,
                columnKey: filter.columnKey ?? "",
                logic: filter.logic ?? "equals",
                formField: filter.formField ?? "",
            };
        }
        return {
            isReference: false,
            columnKey: "",
            logic: "equals",
            formField: "",
        };
    };

    return (
        <>
            <div className="justify-start text-secondary-text text-sm font-medium">
                Filter Configuration
            </div>

            <div className="flex items-center justify-between w-full">
                <Label className="text-sm font-medium">Filter</Label>
                <CodeToggle
                    value={filter?.isReference ? "code" : "manual"}
                    onChange={(value) => {
                        if (value === "code") {
                            onChangeFilter({
                                isReference: true,
                                expression:
                                    filter && filter.isReference
                                        ? filter.expression
                                        : DEFAULT_GRID_FILTER_TEMPLATE,
                            } satisfies DynamicDatasourceFilterExpression);
                            return;
                        }
                        onChangeFilter(getCurrentUiFilter());
                    }}
                />
            </div>

            {filter?.isReference ? (
                <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                    <Label className="text-dark text-base font-medium">
                        Expression
                    </Label>
                    <CodeEditButton
                        variant="reference"
                        value={
                            filter.expression ?? DEFAULT_GRID_FILTER_TEMPLATE
                        }
                        trigger={
                            filter.expression ?? DEFAULT_GRID_FILTER_TEMPLATE
                        }
                        onSave={(nextExpression) => {
                            onChangeFilter({
                                isReference: true,
                                expression: nextExpression,
                            } satisfies DynamicDatasourceFilterExpression);
                        }}
                    />
                </div>
            ) : (
                <>
                    <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                        <Label className="text-dark text-base font-medium">
                            Filter Value Source
                        </Label>
                        <Select
                            options={columnOptions}
                            value={filter?.columnKey}
                            placeholder="Select a table column"
                            onChange={(nextColumn: string) => {
                                onChangeFilter({
                                    ...getCurrentUiFilter(),
                                    columnKey: nextColumn,
                                });
                            }}
                        />
                    </div>
                    <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                        <Label className="text-dark text-base font-medium">
                            Operator
                        </Label>
                        <Select
                            options={operatorOptions}
                            value={filter?.logic ?? undefined}
                            placeholder="Select operator"
                            onChange={(nextOperator: string) => {
                                onChangeFilter({
                                    ...getCurrentUiFilter(),
                                    logic: nextOperator,
                                });
                            }}
                        />
                    </div>
                    <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                        <Label className="text-dark text-base font-medium">
                            Referenced Field
                        </Label>
                        <Select
                            options={formFieldOptions}
                            value={filter?.formField}
                            placeholder="Select a form field"
                            onChange={(nextField: string) => {
                                onChangeFilter({
                                    ...getCurrentUiFilter(),
                                    formField: nextField,
                                });
                            }}
                        />
                    </div>
                </>
            )}
        </>
    );
}
